import axios from 'axios';
import { API_BASE_URL } from '../config';
import { getToken } from '../utils/auth';

// Types for expense data
export interface ExpenseCategory {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Expense {
  id: number;
  amount: number;
  date: string;
  description?: string;
  paymentMethod: 'cash' | 'bank' | 'credit card' | 'other';
  receipt?: string;
  isRecurring: boolean;
  recurringFrequency?: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually';
  nextRecurringDate?: string;
  expenseCategoryId?: number;
  ExpenseCategoryId?: number;
  userId?: number;
  UserId?: number;
  expenseCategory?: ExpenseCategory;
  ExpenseCategory?: ExpenseCategory;
  createdAt: string;
  updatedAt: string;
}

export interface Invoice {
  id: number;
  invoiceNumber: string;
  payee: string;
  purpose: string;
  amount: number;
  issueDate: string;
  dueDate: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  notes?: string;
  isRecurring: boolean;
  recurringFrequency?: 'weekly' | 'monthly' | 'quarterly' | 'annually';
  nextRecurringDate?: string;
  userId: number;
  createdAt: string;
  updatedAt: string;
}

export interface CashCount {
  id: number;
  date: string;
  expectedAmount: number;
  actualAmount: number;
  discrepancy: number;
  count50s: number;
  count20s: number;
  count10s: number;
  count5s: number;
  count1s: number;
  countCoins: number;
  notes?: string;
  userId: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProfitData {
  timePeriod: string;
  revenue: number;
  expenses: number;
  cogs: number;
  grossProfit: number;
  netProfit: number;
}

export interface ProfitResponse {
  profitByPeriod: ProfitData[];
  totals: {
    revenue: number;
    expenses: number;
    cogs: number;
    grossProfit: number;
    netProfit: number;
  };
}

// Expense Categories API
export const getExpenseCategories = async (): Promise<ExpenseCategory[]> => {
  try {
    const token = getToken();
    const response = await axios.get(`${API_BASE_URL}/expenses/categories`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching expense categories:', error);
    throw error;
  }
};

export const createExpenseCategory = async (categoryData: Partial<ExpenseCategory>): Promise<ExpenseCategory> => {
  try {
    const token = getToken();
    const response = await axios.post(`${API_BASE_URL}/expenses/categories`, categoryData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error creating expense category:', error);
    throw error;
  }
};

export const updateExpenseCategory = async (id: number, categoryData: Partial<ExpenseCategory>): Promise<void> => {
  try {
    const token = getToken();
    await axios.put(`${API_BASE_URL}/expenses/categories/${id}`, categoryData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  } catch (error) {
    console.error(`Error updating expense category with id ${id}:`, error);
    throw error;
  }
};

export const deleteExpenseCategory = async (id: number): Promise<void> => {
  try {
    const token = getToken();
    await axios.delete(`${API_BASE_URL}/expenses/categories/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  } catch (error) {
    console.error(`Error deleting expense category with id ${id}:`, error);
    throw error;
  }
};

// Expenses API
export const getExpenses = async (
  startDate?: string,
  endDate?: string,
  categoryId?: number
): Promise<Expense[]> => {
  try {
    const token = getToken();
    let url = `${API_BASE_URL}/expenses`;
    
    // Add filters if provided
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (categoryId) params.append('categoryId', categoryId.toString());
    
    console.log(`Fetching expenses from ${startDate} to ${endDate} ${categoryId ? `for category ${categoryId}` : ''}`);
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching expenses:', error);
    
    // If it's a server error (500), return empty array with warning
    if (error.response && error.response.status === 500) {
      console.warn(`Server error (500) when fetching expenses. This may be due to invalid date parameters or server issues. Returning empty array instead of crashing.`);
      return [];
    }
    
    // For other errors, still throw to be handled upstream
    throw error;
  }
};

export const getExpense = async (id: number): Promise<Expense> => {
  try {
    const token = getToken();
    const response = await axios.get(`${API_BASE_URL}/expenses/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching expense with id ${id}:`, error);
    throw error;
  }
};

export const createExpense = async (expenseData: Partial<Expense>): Promise<Expense> => {
  try {
    const token = getToken();
    const response = await axios.post(`${API_BASE_URL}/expenses`, expenseData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error creating expense:', error);
    throw error;
  }
};

export const updateExpense = async (id: number, expenseData: Partial<Expense>): Promise<void> => {
  try {
    const token = getToken();
    await axios.put(`${API_BASE_URL}/expenses/${id}`, expenseData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  } catch (error) {
    console.error(`Error updating expense with id ${id}:`, error);
    throw error;
  }
};

export const deleteExpense = async (id: number): Promise<void> => {
  try {
    const token = getToken();
    await axios.delete(`${API_BASE_URL}/expenses/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  } catch (error) {
    console.error(`Error deleting expense with id ${id}:`, error);
    throw error;
  }
};

export const getExpenseSummaryByCategory = async (
  startDate?: string,
  endDate?: string
): Promise<{totalAmount: number, expenseCategory: ExpenseCategory}[]> => {
  try {
    const token = getToken();
    let url = `${API_BASE_URL}/expenses/summary-by-category`;
    
    // Add filters if provided
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    console.log(`Fetching expense summary from ${startDate} to ${endDate}`);
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    // Ensure the response is properly formatted - defensive programming
    let safeData = response.data;
    if (!Array.isArray(safeData)) {
      console.warn('Expense summary response is not an array, using empty array instead');
      safeData = [];
    }
    
    // Ensure each item has the required properties
    return safeData.map((item: any) => {
      if (!item.expenseCategory) {
        return {
          totalAmount: Number(item.totalAmount || 0),
          expenseCategory: {
            id: 0,
            name: 'Uncategorized',
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
        };
      }
      return item;
    });
  } catch (error: any) {
    console.error('Error fetching expense summary by category:', error);
    
    // If it's a server error (500), return empty result with warning
    if (error.response && error.response.status === 500) {
      console.warn(`Server error (500) when fetching expense summary. This may be due to invalid date parameters or server issues. Returning empty data instead of crashing.`);
      return [];
    }
    
    // For other errors, still throw to be handled upstream
    throw error;
  }
};

// Invoices API
export const getInvoices = async (
  startDate?: string,
  endDate?: string,
  status?: string
): Promise<Invoice[]> => {
  try {
    const token = getToken();
    let url = `${API_BASE_URL}/invoices`;
    
    // Add filters if provided
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (status) params.append('status', status);
    
    // Debug log the API URL
    const requestUrl = `${url}?${params.toString()}`;
    console.log(`Fetching invoices from: ${requestUrl}`);
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    // Log response for debugging
    console.log(`Received ${response.data.length} invoices from server`);
    if (response.data.length > 0) {
      console.log('First invoice example:', JSON.stringify(response.data[0]));
    } else {
      console.log('No invoices returned from server');
    }
    
    return response.data;
  } catch (error: any) {
    console.error('Error fetching invoices:', error);
    
    // Log more detailed error information for debugging
    if (error.response) {
      console.error(`Server responded with status ${error.response.status}`);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received from server');
    } else {
      console.error('Error message:', error.message);
    }
    
    // If it's a server error (500), return empty array with warning
    if (error.response && error.response.status === 500) {
      console.warn(`Server error (500) when fetching invoices. This may be due to invalid date parameters or server issues. Returning empty array instead of crashing.`);
      return [];
    }
    
    // For other errors, still throw to be handled upstream
    throw error;
  }
};

// Profit calculation API
export const getProfitCalculation = async (
  startDate: string,
  endDate: string,
  interval: 'daily' | 'weekly' | 'monthly' | 'yearly' = 'monthly'
): Promise<ProfitResponse> => {
  try {
    const token = getToken();
    // Log the request parameters
    console.log(`Fetching profit calculation from ${startDate} to ${endDate} with interval ${interval}`);
    
    const response = await axios.get(`${API_BASE_URL}/accounting/profit`, {
      params: { startDate, endDate, interval },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching profit calculation:', error);
    
    // If it's a server error (500), return empty result with warning
    if (error.response && error.response.status === 500) {
      console.warn(`Server error (500) when fetching profit calculation. This may be due to invalid date parameters or server issues. Returning empty data instead of crashing.`);
      return {
        profitByPeriod: [],
        totals: {
          revenue: 0,
          expenses: 0,
          cogs: 0,
          grossProfit: 0,
          netProfit: 0
        }
      };
    }
    
    // For other errors, still throw to be handled upstream
    throw error;
  }
}; 